import React, { useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import ItemList from 'Components/Product/Seo/ItemList';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import HitCard from 'Components/Cards/HitCard';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import useViewport from 'Hooks/useViewport';

export default function TopSellersTop({
  hits,
  gameHits,
  gameBundles,
  booksComicsHits,
  otherHits,
}) {
  const [topSellerTab, setTopSellerTab] = useState('all');

  const { isMd, isSm, isAboveMd } = useViewport();

  let sliceLimit = 11;
  if (isSm || isMd) sliceLimit = 10;

  let topSellerProducts = hits;
  let viewAllLink = '/search';
  if (topSellerTab === 'all') {
    topSellerProducts = hits;
    viewAllLink = '/search';
  }
  if (topSellerTab === 'games') {
    topSellerProducts = gameHits;
    viewAllLink = '/search?types=game%2Cdlc';
  }
  if (topSellerTab === 'gamebundles') {
    topSellerProducts = gameBundles;
    viewAllLink = '/search?types=bundle';
  }
  if (topSellerTab === 'bookscomics') {
    topSellerProducts = booksComicsHits;
    viewAllLink = '/search?types=book%2Ccomic%2Cbook-bundle%2Cgraphic-novel%2Ccomic-bundle';
  }
  if (topSellerTab === 'other') {
    topSellerProducts = otherHits;
    viewAllLink = '/search?types=audio%2Caudio-bundle%2Csoftware%2Csoftware-bundle%2Celearning-bundle%2Celearning';
  }

  return (
    <React.Fragment>
      <section className="container ProductTabsSection">
        <div className="ProductTabsSection__titleView">
          <h2>
            <FormattedMessage
              id="top-sellers-top.title.top-sellers-today"
              defaultMessage="Top Sellers"
            />
          </h2>
          {!isAboveMd &&
            <Link to={viewAllLink} className="ProductTabsSection__viewLink">
              <FormattedMessage
                id="top-sellers-top.link.view-all"
                defaultMessage="View all"
              />
            </Link>
          }
        </div>

        <div className="ProductTabsSection__tabsView">
          <div className="ProductTabsSection__tabs">
            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': topSellerTab === 'all' })} onClick={() => { setTopSellerTab('all'); }}>
              <FormattedMessage id="top-sellers-top.tab.all" defaultMessage="All Products" />
            </button>
            <ItemList hits={hits} title="Top Sellers" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': topSellerTab === 'games' })} onClick={() => { setTopSellerTab('games'); }}>
              <FormattedMessage id="top-sellers-top.tab.games" defaultMessage="Games" />
            </button>
            <ItemList hits={gameHits} title="Game Top Sellers" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': topSellerTab === 'gamebundles' })} onClick={() => { setTopSellerTab('gamebundles'); }}>
              <FormattedMessage id="top-sellers-top.tab.game-bundles" defaultMessage="Game Bundles" />
            </button>
            <ItemList hits={gameBundles} title="Game Bundle Top Sellers" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': topSellerTab === 'bookscomics' })} onClick={() => { setTopSellerTab('bookscomics'); }}>
              <FormattedMessage id="top-sellers-top.tab.books-comics" defaultMessage="Books & Comics" />
            </button>
            <ItemList hits={booksComicsHits} title="Books & Comics Top Sellers" />

            <button className={classNames('ProductTabsSection__tabs__tab', { 'ProductTabsSection__tabs__tab--active': topSellerTab === 'other' })} onClick={() => { setTopSellerTab('other'); }}>
              <FormattedMessage id="top-sellers-top.tab.other" defaultMessage="Other" />
            </button>
            <ItemList hits={otherHits} title="Other Top Sellers" />
          </div>

          {isAboveMd &&
            <Link to={viewAllLink} className="ProductTabsSection__viewLink">
              <FormattedMessage
                id="top-sellers-top.link.view-all"
                defaultMessage="View all"
              />
            </Link>
          }
        </div>

        {isEmpty(topSellerProducts) &&
          <div className="row">
            <SkeletonHitCard
              numberOfCards={1}
              xsSize={12}
              smSize={6}
              mdSize={6}
              lgSize={4}
            />
            <SkeletonHitCard
              numberOfCards={1}
              xsSize={6}
              smSize={6}
              mdSize={6}
              lgSize={4}
            />
            <SkeletonHitCard
              numberOfCards={1}
              xsSize={6}
              mdSize={6}
              lgSize={4}
            />
            <SkeletonHitCard
              numberOfCards={8}
              xsSize={6}
              mdSize={6}
              lgSize={3}
            />
          </div>
        }

        <ul className="HitCardsRow HitCardsRow--marginReset row">
          {topSellerProducts.slice(0, 1).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={12}
              smSize={6}
              mdSize={6}
              lgSize={4}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            >
              <PriceContainer
                discountPercent={hit.discount_percent}
                price={hit.price}
                tiered={hit.tiered}
                slug={hit.slug}
                hideDiscount={hit.hide_discount}
                payWhatYouWant={hit.pay_what_you_want}
              />
            </HitCard>,
          )}
          {topSellerProducts.slice(1, 2).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={6}
              lgSize={4}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            >
              <PriceContainer
                discountPercent={hit.discount_percent}
                price={hit.price}
                tiered={hit.tiered}
                slug={hit.slug}
                hideDiscount={hit.hide_discount}
                payWhatYouWant={hit.pay_what_you_want}
              />
            </HitCard>,
          )}
          {topSellerProducts.slice(2, 3).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={6}
              lgSize={4}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            >
              <PriceContainer
                discountPercent={hit.discount_percent}
                price={hit.price}
                tiered={hit.tiered}
                slug={hit.slug}
                hideDiscount={hit.hide_discount}
                payWhatYouWant={hit.pay_what_you_want}
              />
            </HitCard>,
          )}
          {topSellerProducts.slice(3, sliceLimit).map(hit =>
            <HitCard
              key={hit.objectID}
              hit={hit}
              xsSize={6}
              smSize={6}
              mdSize={6}
              lgSize={3}
              hitCardContainerTag="li"
              hitCardNameTag="h3"
            >
              <PriceContainer
                discountPercent={hit.discount_percent}
                price={hit.price}
                tiered={hit.tiered}
                slug={hit.slug}
                hideDiscount={hit.hide_discount}
                payWhatYouWant={hit.pay_what_you_want}
              />
            </HitCard>,
          )}
        </ul>
      </section>
    </React.Fragment>
  );
}

TopSellersTop.propTypes = {
  hits: PropTypes.arrayOf(PropTypes.object),
  gameHits: PropTypes.arrayOf(PropTypes.object),
  gameBundles: PropTypes.arrayOf(PropTypes.object),
  booksComicsHits: PropTypes.arrayOf(PropTypes.object),
  otherHits: PropTypes.arrayOf(PropTypes.object),
};

TopSellersTop.defaultProps = {
  hits: [],
  gameHits: [],
  gameBundles: [],
  booksComicsHits: [],
  otherHits: [],
};
